// Provider + hook co-location is intentional for auth context
/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { Sentry, enableSessionReplay } from '@/lib/sentry';
import { posthog, initPostHog } from '@/lib/posthog';
import { doorRefusalFrom } from '@/lib/auth/door';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  sendEmailCode: (email: string) => Promise<{ error: AuthError | null }>;
  verifyEmailCode: (email: string, token: string) => Promise<{ error: AuthError | null }>;
  signInWithProvider: (provider: 'google') => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Check localStorage synchronously to avoid spinner flash for returning users
const getInitialAuthState = (): { user: User | null; session: Session | null; loading: boolean } => {
  try {
    const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (storageKey) {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '');
      if (stored?.user) {
        return { user: stored.user, session: stored, loading: false };
      }
    }
  } catch {
    // Ignore localStorage parsing errors — fall through to default loading state
  }
  return { user: null, session: null, loading: true };
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [initialState] = useState(getInitialAuthState);
  const [user, setUser] = useState<User | null>(initialState.user);
  const [session, setSession] = useState<Session | null>(initialState.session);
  const [loading, setLoading] = useState(initialState.loading);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Identify user in observability tools. PostHog + Sentry session
        // replay only initialise once a real authenticated session exists —
        // before then (public showcase, booking, site-preview), Tier B has
        // zero non-essential client-side state.
        if (session?.user) {
          initPostHog();
          enableSessionReplay();
          Sentry.setUser({ id: session.user.id, email: session.user.email });
          if (posthog.__loaded) {
            posthog.identify(session.user.id, { email: session.user.email });
          }
        } else {
          Sentry.setUser(null);
          if (posthog.__loaded) {
            posthog.reset();
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Passwordless email. The `send-otp` edge function generates the 6-digit code server-side
  // (admin.generateLink — does NOT send an email) and delivers it branded via Resend, so the
  // email matches the 6-digit UI instead of Supabase's default magic-link template. It creates a
  // new user OR targets an existing one (same as the old signInWithOtp shouldCreateUser path), so
  // there is still no "sign in vs sign up" distinction. verifyEmailCode then exchanges the code
  // for a session via verifyOtp — verification is unchanged.
  const sendEmailCode = async (email: string) => {
    // Try the branded 6-digit code first (send-otp → Resend). Until a Resend domain is verified,
    // Resend can only deliver to the account owner's address, so on ANY failure fall back to
    // Supabase's own mailer (signInWithOtp) — that delivers to any recipient, so login never
    // breaks. Once RESEND_FROM_DOMAIN is set, send-otp succeeds for everyone and the fallback
    // stops firing. verifyEmailCode (verifyOtp) validates whichever code/link was issued.
    const { error } = await supabase.functions.invoke('send-otp', { body: { email } });
    if (!error) return { error: null };
    // The door (2026-10-04): a 403 from send-otp is the invite-only rule
    // refusing a stranger's account, not a delivery failure. It is returned
    // as is, never retried through Supabase's mailer (which would ask the
    // same rule as the before-user-created hook and get the same answer).
    const refusal = await doorRefusalFrom(error);
    if (refusal) return { error: new AuthError(refusal, 403, 'signup_disabled') };
    // The fallback mails a code to an account that EXISTS; it makes none.
    // Accounts are made by send-otp (which asks the door) or by Google (the
    // hook); a stranger the fallback meets is told so by Supabase.
    return await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
  };

  const verifyEmailCode = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    return { error };
  };

  const signInWithProvider = async (provider: 'google') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
        // select_account: always show Google's account chooser. Without a prompt
        // param Google silently reuses the browser's last-used account — signing
        // into the wrong Contruly identity with no way to switch (2026-08-06).
        // Deliberately NOT 'consent': that re-nags scope approval on every login
        // (removed 2026-08-03); the chooser is the middle setting.
        queryParams: { prompt: 'select_account' },
      },
    });
    return { error };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const value = {
    user,
    session,
    loading,
    sendEmailCode,
    verifyEmailCode,
    signInWithProvider,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
